/**
 * Per-engine statement parsers used by `scriptScope.ts`.
 *
 * Each parser takes a single, pre-trimmed statement string and returns either
 * the extracted scope value (database name / schema name / search-path list /
 * attach descriptor) or `null` when the statement isn't a recognized scope
 * directive. Quoted-identifier forms (`[…]` MSSQL / SQLite, `"…"`, `` `…` ``,
 * `'…'`) are unwrapped; doubled-quote escapes are honored.
 */

const USE_RE = /^use\s+(\[[^\]]*(?:\]\][^\]]*)*\]|"(?:[^"]|"")*"|`(?:[^`]|``)*`|[^\s;]+)\s*;?$/i;

export function parseUseDatabase(stmt: string): string | null {
  const m = stmt.match(USE_RE);
  if (!m) return null;
  return unquote(m[1]!.trim());
}

const SET_SEARCH_PATH_RE = /^set\s+search_path\s*(?:=|to)\s+(.+?)\s*;?$/i;

export function parseSetSearchPath(stmt: string): string[] | null {
  const m = stmt.match(SET_SEARCH_PATH_RE);
  if (!m) return null;
  const parts = m[1]!
    .split(',')
    .map((p) => unquote(p.trim()))
    .filter((p) => p.length > 0);
  return parts.length === 0 ? null : parts;
}

const ALTER_SESSION_CURRENT_SCHEMA_RE =
  /^alter\s+session\s+set\s+current_schema\s*=\s*("(?:[^"]|"")*"|[^\s;]+)\s*;?$/i;

export function parseAlterSessionCurrentSchema(stmt: string): string | null {
  const m = stmt.match(ALTER_SESSION_CURRENT_SCHEMA_RE);
  if (!m) return null;
  return unquote(m[1]!.trim());
}

const ATTACH_DATABASE_RE =
  /^attach\s+(?:database\s+)?('(?:[^']|'')*'|"(?:[^"]|"")*"|\S+?)\s+as\s+([^\s;]+)\s*;?$/i;

export function parseAttachDatabase(stmt: string): { alias: string; path: string } | null {
  const m = stmt.match(ATTACH_DATABASE_RE);
  if (!m) return null;
  const path = unquoteString(m[1]!.trim());
  const alias = unquote(m[2]!.trim());
  return { alias, path };
}

function unquote(id: string): string {
  if (id.length < 2) return id;
  const first = id.charCodeAt(0);
  const last = id.charCodeAt(id.length - 1);
  if (first === 0x5b /* [ */ && last === 0x5d /* ] */) {
    return id.slice(1, -1).replace(/\]\]/g, ']');
  }
  if (first === 0x22 /* " */ && last === 0x22) {
    return id.slice(1, -1).replace(/""/g, '"');
  }
  if (first === 0x60 /* ` */ && last === 0x60) {
    return id.slice(1, -1).replace(/``/g, '`');
  }
  if (first === 0x27 /* ' */ && last === 0x27) {
    return id.slice(1, -1).replace(/''/g, "'");
  }
  return id;
}

function unquoteString(value: string): string {
  if (value.length < 2) return value;
  const first = value.charCodeAt(0);
  const last = value.charCodeAt(value.length - 1);
  if (first === 0x27 && last === 0x27) {
    return value.slice(1, -1).replace(/''/g, "'");
  }
  if (first === 0x22 && last === 0x22) {
    return value.slice(1, -1).replace(/""/g, '"');
  }
  return value;
}

/**
 * Strip leading whitespace, line comments, and block comments AND trim
 * trailing whitespace from a statement slice. The splitter keeps these in
 * the slice and they'd otherwise foil the anchored regex parsers.
 */
export function stripLeadingNoise(stmt: string): string {
  let i = 0;
  const length = stmt.length;
  while (i < length) {
    const c = stmt.charCodeAt(i);
    if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) {
      i += 1;
      continue;
    }
    if (c === 0x2d && stmt.charCodeAt(i + 1) === 0x2d) {
      while (i < length && stmt.charCodeAt(i) !== 0x0a) i += 1;
      continue;
    }
    if (c === 0x2f && stmt.charCodeAt(i + 1) === 0x2a) {
      i += 2;
      while (i < length && !(stmt.charCodeAt(i) === 0x2a && stmt.charCodeAt(i + 1) === 0x2f)) i += 1;
      i += 2;
      continue;
    }
    break;
  }
  let j = length;
  while (j > i) {
    const c = stmt.charCodeAt(j - 1);
    if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) {
      j -= 1;
      continue;
    }
    break;
  }
  return stmt.slice(i, j);
}
